import {MinusIcon, PlusIcon} from '@heroicons/react/solid';
import {useState} from 'react';

import {Button, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import type {MarketPlaceBid} from '@/types/MarketPlace';
import {MarketPlaceStatus} from '@/types/MarketPlace';

import {formatOptionalDate} from '../utils/date';
import {formatAmount} from '../utils/format';
import {getBidStatusDisplay} from '../utils/status';
import BidSummary from './BidSummary';

// The price a bid was agreed at: the accepted offer price (the counter price
// once approved) when set, else the ask's quote. Takes `ask` separately
// (rather than reading `bid.ask` internally) so a caller that has already
// checked `bid.ask` is truthy can pass the narrowed value straight through.
const acceptedPrice = (
  bid: MarketPlaceBid,
  ask: NonNullable<MarketPlaceBid['ask']>,
) =>
  bid.meta?.acceptedOfferPrice
    ? {asset: ask.quote.asset, value: bid.meta.acceptedOfferPrice}
    : ask.quote;

type BidTableProps = {
  bids: MarketPlaceBid[];
  loading?: boolean;
  onApprove?: (bid: MarketPlaceBid) => void;
  onReject?: (bid: MarketPlaceBid) => void;
};

const BidTableRow = ({
  bid,
  onApprove,
  onReject,
}: {
  bid: MarketPlaceBid;
  onApprove?: (bid: MarketPlaceBid) => void;
  onReject?: (bid: MarketPlaceBid) => void;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const status = getBidStatusDisplay(
    bid.status,
    bid.expiresAt,
    bid.meta?.flags?.hasExpired,
  );

  return (
    <>
      <Tr>
        <Td isNumeric className="px-4">
          <Button onClick={() => setIsExpanded(!isExpanded)} variant="unStyled">
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight dark:text-gray-100 text-gray-700 w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                isExpanded && 'bg-cyan-50'
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4" />
              ) : (
                <PlusIcon className="w-4 h-4" />
              )}
            </div>
          </Button>
        </Td>
        <Td>{bid.id}</Td>
        <Td>
          {bid.ask
            ? `${formatAmount(bid.ask.volume)} @ ${formatAmount(acceptedPrice(bid, bid.ask))}`
            : '-'}
        </Td>
        <Td>
          <div className="flex flex-col">
            <span className="font-medium">{bid.businessName || '-'}</span>
            <span className="text-xs text-gray-500">{bid.userId}</span>
          </div>
        </Td>
        <Td>
          {bid.bid.value} {bid.bid.asset}
        </Td>
        <Td>{bid.meta?.counterOfferPrice ?? '-'}</Td>
        <Td>
          <span className={status.className}>{status.label}</span>
        </Td>
        <Td>{formatOptionalDate(bid.createdAt)}</Td>
        <Td>
          {bid.status === MarketPlaceStatus.PENDING_ADMIN_APPROVAL && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                colorScheme="green"
                onClick={() => onApprove?.(bid)}
              >
                Approve
              </Button>
              <Button
                variant="outline"
                colorScheme="red"
                onClick={() => onReject?.(bid)}
              >
                Reject
              </Button>
            </div>
          )}
        </Td>
      </Tr>

      {isExpanded && (
        <Tr enableHover={false}>
          <Td colSpan={9} className="p-0 m-0">
            <div className="flex py-5 sm:py-10 px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
              <BidSummary bid={bid} />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

const BidTable = ({bids, loading, onApprove, onReject}: BidTableProps) => {
  if (loading) {
    return (
      <Table>
        <Thead>
          <Tr enableHover={false}>
            <Th className="w-20 px-4">&nbsp;</Th>
            <Th>ID</Th>
            <Th>Offer</Th>
            <Th>Business</Th>
            <Th>Bid</Th>
            <Th>Counter Offer</Th>
            <Th>Status</Th>
            <Th>Created At</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <TBody>
          <Tr>
            <Td colSpan={9} className="text-center text-gray-500 py-4">
              Loading...
            </Td>
          </Tr>
        </TBody>
      </Table>
    );
  }

  return (
    <Table>
      <Thead>
        <Tr enableHover={false}>
          <Th className="w-20 px-4">&nbsp;</Th>
          <Th>ID</Th>
          <Th>Offer</Th>
          <Th>Business</Th>
          <Th>Bid</Th>
          <Th>Counter Offer</Th>
          <Th>Status</Th>
          <Th>Created At</Th>
          <Th>Actions</Th>
        </Tr>
      </Thead>
      <TBody>
        {bids.length === 0 ? (
          <Tr>
            <Td colSpan={9} className="text-center text-gray-500 py-4">
              No bids found
            </Td>
          </Tr>
        ) : (
          bids.map((bid) => (
            <BidTableRow
              key={bid.id}
              bid={bid}
              onApprove={onApprove}
              onReject={onReject}
            />
          ))
        )}
      </TBody>
    </Table>
  );
};

export default BidTable;
